import { createRouter, createWebHistory } from 'vue-router';

import AccessDeniedPage from '@/core/pages/AccessDeniedPage.vue';
import NotFoundPage from '@/core/pages/NotFoundPage.vue';
import ServerErrorPage from '@/core/pages/ServerErrorPage.vue';

import { authMiddleware } from '@/core/middlewares/auth';
import { permissionMiddleware } from '@/core/middlewares/permission';
import { registerErrorMiddleware } from '@/shared/middlewares/error';
import { useErrorStore } from '@/shared/stores/error';

// The feature routes are added at runtime by `registerModules` (main.ts). Only
// the routes that belong to no module live here.
const router = createRouter({
  history: createWebHistory(),
  // A hash lands on a section (the cheat sheet links into the theory page);
  // the offset clears the sticky mobile top bar. Instant, not smooth: a smooth
  // scroll never starts in a background tab and can land off on a long page.
  // A query-only change (a switcher on the same page) keeps the position.
  scrollBehavior: (to, from, saved) => {
    if (saved) return saved;
    if (to.hash) return { el: to.hash, top: 72 };
    if (to.path === from.path) return false;
    return { top: 0 };
  },
  routes: [
    {
      path: '/access-denied',
      name: 'AccessDenied',
      component: AccessDeniedPage,
      meta: { layout: 'empty', public: true },
    },
    {
      path: '/server-error',
      name: 'ServerError',
      component: ServerErrorPage,
      meta: { layout: 'empty', public: true },
    },
    // Catch-all 404. Vue Router scores matchers rather than trusting insertion
    // order, so the module routes added later still win over it.
    {
      path: '/:pathMatch(.*)*',
      name: 'NotFound',
      component: NotFoundPage,
      meta: { layout: 'main' },
    },
  ],
});

// Clears stale errors first, before any guard that may set a new one.
registerErrorMiddleware(router);
router.beforeEach(authMiddleware);
router.beforeEach(permissionMiddleware);

// A deploy replaces the hashed chunks under the open tab's feet, so the next
// lazy route import 404s. There is nothing to retry — the old bundle is gone.
router.onError((error) => {
  if (
    error.message.includes('Failed to fetch dynamically imported module')
    || error.message.includes('Importing a module script failed')
  ) {
    useErrorStore().setError({
      statusCode: 418,
      title: 'Вышла новая версия',
      description: 'Приложение обновилось. Перезагрузи страницу, чтобы продолжить.',
      critical: true,
    });
  }
});

export default router;
